/**
 * QUIPWA — sync status, always visible
 *
 * Desktop: lives in the sidebar. A dot + label tells the state at a glance;
 * clicking opens a panel with the detail, the last sync time, queued changes
 * and the action that fixes the state (reconnect, grant Drive access…).
 *
 * Phone: `compact`, in the top bar. The icon alone (the label becomes its
 * accessible name), tinted amber when the state needs the user, as on desktop;
 * the panel drops below the bar at full width.
 *
 * The copy is honest about the architecture: there is no server, so "saved"
 * means "on this device" until a sync confirms it is in Drive.
 */
import { useEffect, useId, useRef, useState } from 'react'
import { AlertTriangle, CloudOff, KeyRound, LogIn, RefreshCw, Check, CloudUpload } from 'lucide-react'
import { useSyncStatus, type SyncStatus } from '../../lib/sync/sync-status-store.ts'
import { useI18n } from '../../lib/i18n/i18n-store.ts'
import { formatRelative } from '../../lib/i18n/relative-time.ts'
import { useSyncActions } from './useSyncActions.ts'
import './SyncStatusChip.css'

const ICON: Record<SyncStatus, typeof Check> = {
  synced: Check,
  syncing: RefreshCw,
  pending: CloudUpload,
  offline: CloudOff,
  needs_auth: KeyRound,
  needs_consent: KeyRound,
  signed_out: LogIn,
  error: AlertTriangle,
}

/** The «Fichero» skin's activity LED (hidden in the flat skin): steady green
 *  in sync, amber beating slowly while changes wait, red when it needs the
 *  user, off with no account or no network. */
const LED: Record<SyncStatus, string> = {
  synced: 'led--on',
  syncing: 'led--amber led--blink',
  pending: 'led--amber led--blink',
  offline: '',
  needs_auth: 'led--red',
  needs_consent: 'led--red',
  signed_out: '',
  error: 'led--red',
}

export function SyncStatusChip({ compact = false }: { compact?: boolean }) {
  const { t, locale } = useI18n()
  const { status, primary, syncNow } = useSyncActions()
  const lastSyncedAt = useSyncStatus((s) => s.lastSyncedAt)
  const pendingOps = useSyncStatus((s) => s.pendingOps)
  const parkedOps = useSyncStatus((s) => s.parkedOps)
  const [open, setOpen] = useState(false)
  const panelId = useId()
  const rootRef = useRef<HTMLDivElement>(null)
  const Icon = ICON[status]

  useEffect(() => {
    if (!open) return
    // pointerdown, not mousedown: a touch closes it on contact, as a click does.
    const onDown = (e: PointerEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false)
    document.addEventListener('pointerdown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('pointerdown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  const label = t(`sync.status.${status}`)
  return (
    <div className={`syncchip-root${compact ? ' syncchip-root--compact' : ''}`} ref={rootRef}>
      <button
        className={`syncchip syncchip--${status}${compact ? ' syncchip--compact' : ''}`}
        aria-expanded={open}
        aria-controls={panelId}
        aria-label={compact ? t('sync.chipLabel', { status: label }) : undefined}
        title={compact ? label : undefined}
        onClick={() => setOpen((o) => !o)}
      >
        <span className={`led ${LED[status]}`} aria-hidden="true" />
        <Icon size={compact ? 18 : 15} className={status === 'syncing' ? 'syncchip-spin' : undefined} aria-hidden="true" />
        {!compact && <span className="syncchip-label">{label}</span>}
        {(pendingOps > 0 || parkedOps > 0) && status !== 'syncing' && (
          <span className="syncchip-count" aria-hidden="true">{pendingOps + parkedOps}</span>
        )}
      </button>

      {open && (
        <div id={panelId} className="syncchip-panel" role="dialog" aria-label={label}>
          <p className="syncchip-detail">{t(`sync.detail.${status}`)}</p>
          <p className="syncchip-meta">
            {lastSyncedAt ? t('sync.lastSynced', { when: formatRelative(lastSyncedAt, locale) }) : t('sync.never')}
          </p>
          {pendingOps > 0 && <p className="syncchip-meta">{t('sync.pendingOps', { count: pendingOps })}</p>}
          {parkedOps > 0 && <p className="syncchip-meta syncchip-meta--warn">{t('sync.parkedOps', { count: parkedOps })}</p>}
          <div className="syncchip-actions">
            {primary && (
              <button className="btn btn--primary btn--sm" onClick={() => { setOpen(false); primary.run() }}>
                {primary.label}
              </button>
            )}
            {(status === 'synced' || status === 'pending' || status === 'offline') && (
              <button className="btn btn--sm" onClick={() => void syncNow()} disabled={status === 'offline'}>
                <RefreshCw size={13} aria-hidden="true" />
                {t('sync.action.syncNow')}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
